import React from 'react';

const ClientSummary = ({
  isLoading,
  shortSummary,
  longSummary,
  showShortSummary,
  showLongSummary,
  textColor,
  customerData,
  isIBRole
}) => {
  const hasError =
    (shortSummary && shortSummary.toLowerCase().includes("error")) ||
    (longSummary && longSummary.toLowerCase().includes("error")) ||
    (shortSummary && shortSummary.toLowerCase().includes("cannot answer")) ||
    (longSummary && longSummary.toLowerCase().includes("cannot answer")) ||
    (shortSummary && shortSummary.toLowerCase().includes("?")) ||
    (longSummary && longSummary.toLowerCase().includes("?")) ||
    (shortSummary && shortSummary.toLowerCase().includes("sorry")) ||
    (longSummary && longSummary.toLowerCase().includes("sorry")) ||
    (shortSummary && shortSummary.toLowerCase().includes("don't know")) ||
    (longSummary && longSummary.toLowerCase().includes("don't know")) ||
    (shortSummary && shortSummary.toLowerCase().includes("safely")) ||
    (longSummary && longSummary.toLowerCase().includes("safely")) ||
    (shortSummary && shortSummary.toLowerCase().includes("i'm an expert")) ||
    (longSummary && longSummary.toLowerCase().includes("i'm an expert"));

  const generateFallbackShortSummary = () => {
    if (!customerData) return isIBRole ? "Institution brief not available." : "Client summary not available.";

    if (isIBRole) {
      const name = customerData.name || 'This institution';
      const pw = parseFloat(customerData.pipeline_weight);
      const es = parseFloat(customerData.engagement_score);
      const alerts = parseInt(customerData.open_alerts, 10);

      const pipelineFmt = !isNaN(pw) && pw > 0
        ? (pw >= 1e9 ? `$${(pw / 1e9).toFixed(1)}B` : pw >= 1e6 ? `$${(pw / 1e6).toFixed(1)}M` : `$${pw.toFixed(0)}`)
        : null;

      const urgency = (!isNaN(alerts) && alerts > 0) ? 'action needed'
        : (!isNaN(es) && es < 4) ? 'low engagement — re-engage'
        : (!isNaN(es) && es >= 7) ? 'strong momentum'
        : 'active coverage';

      return pipelineFmt
        ? `${name} · ${pipelineFmt} pipeline · ${urgency}`
        : `${name} · ${urgency}`;
    }

    const highlights = [];
    if (customerData.name) highlights.push(`${customerData.name.split(' ')[0]}`);
    if (customerData.net_worth_category) highlights.push(`${customerData.net_worth_category.toLowerCase()}-net-worth client`);
    if (customerData.total_aum) highlights.push(`$${(customerData.total_aum / 1000000).toFixed(2)}M AUM`);
    if (customerData.ytd_return) highlights.push(`${customerData.ytd_return}% YTD return`);
    if (customerData.risk_tolerance) highlights.push(`${customerData.risk_tolerance.toLowerCase()} risk tolerance`);
    if (customerData.time_horizon_category) highlights.push(`${customerData.time_horizon_category.toLowerCase()} horizon`);

    return highlights.length > 0
      ? `${highlights.join(', ')} requiring focused attention.`
      : "Client summary not available.";
  };

  const generateFallbackLongSummary = () => {
    if (!customerData) return "Detailed client information not available.";

    if (isIBRole) {
      const fmt = (val) => {
        const n = parseFloat(val);
        if (isNaN(n) || n <= 0) return null;
        if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
        if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
        if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
        return `$${n.toLocaleString()}`;
      };

      const name = customerData.name || 'This institution';
      const pw = fmt(customerData.pipeline_weight);
      const income = fmt(customerData.annual_income);
      const es = parseFloat(customerData.engagement_score);
      const alerts = parseInt(customerData.open_alerts, 10);
      const lastContact = customerData.last_contact_date;

      // Opening — strategic framing
      const engagementLabel = !isNaN(es)
        ? (es >= 7 ? 'a high-engagement account with strong momentum'
          : es >= 4 ? 'a mid-tier engagement account with room to grow'
          : 'a low-engagement account flagged for re-activation')
        : 'a key coverage account';
      let narrative = `${name} is ${engagementLabel}.`;

      // Financial health
      const financials = [pw && `${pw} in active pipeline`, income && `${income} annual revenue`].filter(Boolean);
      if (financials.length > 0) narrative += ` The institution carries ${financials.join(' and ')}.`;

      // Risk signals
      if (!isNaN(alerts) && alerts > 0)
        narrative += ` ${alerts} open alert${alerts > 1 ? 's require' : ' requires'} immediate follow-up before the next call.`;

      // Relationship cadence
      if (lastContact) narrative += ` Last touch: ${lastContact}.`;
      if (customerData.next_meeting?.date) narrative += ` Next coverage call: ${customerData.next_meeting.date}.`;

      return narrative;
    }

    const details = [];

    // Client identity and basic info
    if (customerData.name && customerData.age) {
      details.push(`${customerData.name} (${customerData.age})`);
    } else if (customerData.name) {
      details.push(customerData.name);
    }

    // Financial details
    const financialDetails = [];
    if (customerData.net_worth) financialDetails.push(`net worth of $${(customerData.net_worth / 1000000).toFixed(2)}M`);
    if (customerData.total_aum) financialDetails.push(`$${(customerData.total_aum / 1000000).toFixed(2)}M assets under management`);
    if (customerData.ytd_return) financialDetails.push(`${customerData.ytd_return}% YTD return`);

    if (financialDetails.length > 0) {
      details.push(`has ${financialDetails.join(' with ')}`);
    }

    // Risk and time horizon
    if (customerData.risk_tolerance && customerData.time_horizon_category) {
      details.push(`demonstrates ${customerData.risk_tolerance.toLowerCase()} risk tolerance with a ${customerData.time_horizon_category.toLowerCase()} investment horizon`);
    } else if (customerData.risk_tolerance) {
      details.push(`has ${customerData.risk_tolerance.toLowerCase()} risk tolerance`);
    } else if (customerData.time_horizon_category) {
      details.push(`has a ${customerData.time_horizon_category.toLowerCase()} investment horizon`);
    }

    // Contact information
    const contactDetails = [];
    if (customerData.last_contact_date) contactDetails.push(`last contacted on ${customerData.last_contact_date}`);
    if (customerData.next_meeting && customerData.next_meeting.date) {
      contactDetails.push(`next meeting scheduled for ${customerData.next_meeting.date}`);
    }

    if (contactDetails.length > 0) {
      details.push(contactDetails.join(' with '));
    }

    // Recommendations based on available data
    const recommendations = [];

    if (customerData.risk_tolerance === "High" && customerData.time_horizon_category === "Short-term") {
      recommendations.push("Consider reviewing portfolio allocation to better align high risk tolerance with short-term horizon");
    }

    if (customerData.net_worth && customerData.total_aum) {
      const aumPercentage = (customerData.total_aum / customerData.net_worth * 100).toFixed(0);
      if (aumPercentage < 50) {
        recommendations.push(`Explore opportunities to increase AUM beyond current ${aumPercentage}% of net worth`);
      }
    }

    // Combine everything into a coherent summary
    let summary = details.join(' ');

    if (recommendations.length > 0) {
      summary += `. Recommendations: ${recommendations.join('. ')}.`;
    }

    return summary || "Detailed client information not available.";
  };

  const fallbackShortSummary = generateFallbackShortSummary();
  const fallbackLongSummary = generateFallbackLongSummary();

  const toGBP = (text) => text?.replace(/\$/g, '£') ?? text;

  const displayShortSummary = isIBRole ? toGBP(hasError ? fallbackShortSummary : shortSummary) : (hasError ? fallbackShortSummary : shortSummary);
  const displayLongSummary  = isIBRole ? toGBP(hasError ? fallbackLongSummary  : longSummary)  : (hasError ? fallbackLongSummary  : longSummary);

  return (
    <div className="relative my-1 pr-6">
      {isLoading ? (
        <div className="text-sm text-gray-600 my-1 italic flex items-center after:content-[''] after:animate-ellipsis after:w-6 after:text-left after:inline-block">
          {isIBRole ? 'Generating institution brief...' : 'Generating client insights...'}
        </div>
      ) : (
        <>
          {displayShortSummary && showShortSummary && (
            <p className="text-base text-secondary m-0 leading-relaxed italic text-sm"
              style={{ color: textColor ? `${textColor}99` : 'inherit' }}>
              {displayShortSummary}
            </p>
          )}
          {displayLongSummary && showLongSummary && (
            <div className="text-xs text-gray-600 mt-1 mb-1.5 leading-relaxed max-w-[95%]"
              style={{ color: textColor ? `${textColor}99` : 'inherit' }}>
              <p>{displayLongSummary}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClientSummary;